import React, { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package, 
  ShoppingCart, 
  Users, 
  Settings, 
  Plane,
  Menu,
  X,
  LogOut,
  Home,
  Grid3X3,
  FileText,
  MapPin,
  Truck,
  Store,
  CheckSquare,
  BarChart3,
  Plug,
  Image,
  TrendingUp,
  Layers,
  Tag,
  Search,
  Monitor,
  QrCode,
  Receipt,
  Activity,
  Zap
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';

export default function AdminLayout({ children }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  // Simplified menu - focused on marketplace management
  const menuItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/vendors', label: 'Vendors', icon: Store },
    { path: '/admin/vendor-analytics', label: 'Vendor Analytics', icon: BarChart3 },
    { path: '/admin/product-approvals', label: 'Product Approvals', icon: CheckSquare },
    { path: '/admin/products', label: 'All Products', icon: Package },
    { path: '/admin/orders', label: 'Orders', icon: ShoppingCart },
    { path: '/admin/abandoned-carts', label: 'Abandoned Carts', icon: ShoppingCart },
    { path: '/admin/customers', label: 'Customers', icon: Users },
    { path: '/admin/discounts', label: 'Discounts', icon: Tag },
    { path: '/admin/categories', label: 'Categories', icon: Grid3X3 },
    { path: '/admin/banners', label: 'Banners', icon: Image },
    { path: '/admin/product-highlights', label: 'Highlights', icon: TrendingUp },
    { path: '/admin/bulk-operations', label: 'Bulk Operations', icon: Layers },
    { path: '/admin/shipping-zones', label: 'Shipping', icon: Truck },
    { path: '/admin/packages', label: 'Travel Packages', icon: Plane },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/admin/page-analytics', label: 'Page Analytics', icon: Activity },
    { path: '/admin/business-health', label: 'Business Health', icon: Zap },
    { path: '/admin/integrations', label: 'Integrations', icon: Plug },
    { path: '/admin/seo-settings', label: 'SEO Settings', icon: Search },
    { path: '/admin/content', label: 'Content Pages', icon: FileText },
    { path: '/admin/content-settings', label: 'Settings', icon: Settings },
  ];

  // POS Menu Items (separate section)
  const posMenuItems = [
    { path: '/admin/pos', label: 'POS Dashboard', icon: Monitor },
    { path: '/admin/pos/orders', label: 'POS Orders', icon: Receipt },
    { path: '/admin/pos/managers', label: 'Store Managers', icon: Users },
    { path: '/admin/pos/discounts', label: 'POS Discounts', icon: Tag },
    { path: '/admin/pos/reports', label: 'POS Reports', icon: BarChart3 },
    { path: '/admin/pos/settings', label: 'POS Settings', icon: QrCode },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Header */}
      <header className="bg-white border-b border-gray-200 fixed top-0 left-0 right-0 z-30">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_shopstack-46/artifacts/wfop58ec_image.png" 
                alt="Nammahut Logo" 
                className="h-10 w-10 object-contain"
              />
              <span className="text-xl font-bold text-green-800 hidden sm:block">Admin Panel</span>
            </Link>
          </div>
          
          <div className="flex items-center space-x-4">
            <Link to="/" className="text-sm text-gray-600 hover:text-green-700 flex items-center space-x-1">
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">View Site</span>
            </Link>
            <div className="flex items-center space-x-2 border-l pl-4">
              <div className="w-8 h-8 bg-gradient-to-br from-green-600 to-green-700 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-semibold">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div className="hidden md:block">
                <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.role}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex pt-16">
        {/* Mobile Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-10 lg:hidden top-16"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={`fixed left-0 top-16 bottom-0 w-64 bg-gradient-to-b from-green-900 to-green-800 text-white transition-transform duration-300 z-20 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 flex flex-col`}
        >
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto pb-20">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActivePath(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    active
                      ? 'bg-yellow-400 text-green-900 font-semibold shadow-lg'
                      : 'text-yellow-100 hover:bg-green-800 hover:text-white'
                  }`}
                  onClick={() => {
                    if (window.innerWidth < 1024) {
                      setSidebarOpen(false);
                    }
                  }}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            
            {/* POS Section Divider */}
            <div className="pt-4 pb-2">
              <div className="flex items-center px-4">
                <div className="flex-1 h-px bg-green-700"></div>
                <span className="px-3 text-xs font-semibold text-green-400 uppercase tracking-wider">POS System</span>
                <div className="flex-1 h-px bg-green-700"></div>
              </div>
            </div>
            
            {/* POS Menu Items */}
            {posMenuItems.map((item) => {
              const Icon = item.icon;
              const active = location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    active
                      ? 'bg-violet-500 text-white font-semibold shadow-lg'
                      : 'text-yellow-100 hover:bg-green-800 hover:text-white'
                  }`}
                  onClick={() => {
                    if (window.innerWidth < 1024) {
                      setSidebarOpen(false);
                    }
                  }}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-green-700 bg-green-900">
            <button
              onClick={logout}
              className="flex items-center space-x-3 px-4 py-3 rounded-lg w-full text-yellow-100 hover:bg-green-800 hover:text-white transition-colors duration-200"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-64 w-full">
          <div className="p-4 sm:p-6 md:p-8">
            {children || <Outlet />}
          </div>
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-10 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
